import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Upload, Camera, Sparkles, X, Hand, Lock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Key for localStorage to track if user already did a free scan
const GUEST_SCAN_KEY = 'reveliae_guest_scan';

const GuestScanPage = () => {
  const navigate = useNavigate();
  const [rightPalmImage, setRightPalmImage] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [hasAlreadyScanned, setHasAlreadyScanned] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check if user already did a free scan
  useEffect(() => {
    const existingScan = localStorage.getItem(GUEST_SCAN_KEY);
    if (existingScan) {
      setHasAlreadyScanned(true);
    }
  }, []);

  // Camera configuration (back camera preferred)
  const videoConstraints = {
    facingMode: { ideal: "environment" }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image valide');
        return;
      }
      setRightPalmImage(file);
      setShowWebcam(false);
      toast.success('Image capturée avec succès');
    }
  };

  const captureFromWebcam = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      fetch(imageSrc)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], 'right-palm-capture.jpg', { type: 'image/jpeg' });
          setRightPalmImage(file);
          setShowWebcam(false);
          toast.success('Photo capturée avec succès');
        });
    }
  };

  const analyzeImage = async () => {
    if (!rightPalmImage) {
      toast.error('Veuillez capturer une image de votre paume droite');
      return;
    }

    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('right_palm', rightPalmImage);
      formData.append('is_guest', 'true');
      formData.append('selected_themes', JSON.stringify(['love', 'career', 'health']));

      const response = await axios.post(`${API}/readings/analyze-guest`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store minimal data - only reading ID and timestamp for tracking
      // Insights are passed via React state, not stored in localStorage
      localStorage.setItem(GUEST_SCAN_KEY, JSON.stringify({
        readingId: response.data.id,
        timestamp: Date.now()
        // Note: insights are NOT stored in localStorage for security
      }));

      toast.success('Analyse terminée !');
      navigate('/try/results', { state: { reading: response.data, isGuest: true } });
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error('Erreur lors de l\'analyse. Veuillez réessayer.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // If user already scanned, show message
  if (hasAlreadyScanned) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-12 px-4">
        <div className="max-w-lg mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl border border-emerald-200 shadow-xl p-8"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Lock className="h-10 w-10 text-white" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Vous avez déjà utilisé votre essai gratuit
            </h1>
            
            <p className="text-gray-600 mb-6">
              Créez un compte gratuit pour sauvegarder votre lecture et débloquer toutes les fonctionnalités de LUMINA !
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => {
                  // Pass the stored reading to auth page
                  const storedScan = localStorage.getItem(GUEST_SCAN_KEY);
                  navigate('/auth', { state: { pendingGuestScan: storedScan } });
                }}
                className="w-full py-6 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-lg"
                data-testid="create-account-btn"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Créer un compte gratuit
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  const storedScan = localStorage.getItem(GUEST_SCAN_KEY);
                  if (storedScan) {
                    const data = JSON.parse(storedScan);
                    navigate('/try/results', { 
                      state: { 
                        reading: { id: data.readingId, insights: data.insights },
                        isGuest: true 
                      } 
                    });
                  }
                }}
                className="w-full py-4 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="view-results-btn"
              >
                Revoir mes résultats
              </Button>
              
              <button
                onClick={() => navigate('/')}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Retour à l'accueil
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 rounded-full text-gray-600 hover:bg-emerald-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
              <Sparkles className="h-4 w-4" />
              Essai gratuit
            </span>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Découvrez votre paume
            </h1>
            
            <p className="text-gray-600 max-w-md mx-auto">
              Photographiez votre <strong>paume droite</strong> pour recevoir une première lecture personnalisée
            </p>
          </div>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl border border-emerald-200 shadow-xl overflow-hidden"
        >
          {/* Image Preview Area */}
          <div className="p-6">
            <div className="relative aspect-square max-w-sm mx-auto rounded-2xl overflow-hidden border-2 border-dashed border-emerald-300 bg-emerald-50/50">
              <AnimatePresence mode="wait">
                {showWebcam ? (
                  <motion.div
                    key="webcam"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                  >
                    <Webcam
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                      <Button
                        onClick={captureFromWebcam}
                        className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-white px-6"
                        data-testid="capture-btn"
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        Capturer
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowWebcam(false)}
                        className="rounded-full bg-white/90"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                ) : rightPalmImage ? (
                  <motion.div
                    key="preview"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={URL.createObjectURL(rightPalmImage)}
                      alt="Paume droite"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setRightPalmImage(null)}
                      className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-400 text-white rounded-full shadow-lg transition-colors"
                      data-testid="clear-image-btn"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="absolute bottom-3 left-3 px-3 py-1 bg-emerald-500 text-white text-sm rounded-full">
                      Paume droite
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center"
                  >
                    <div className="w-24 h-24 mb-4 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                      <Hand className="h-12 w-12 text-emerald-600" />
                    </div>
                    <p className="text-emerald-800 font-medium mb-2">
                      Paume droite
                    </p>
                    <p className="text-sm text-emerald-600/80">
                      Photographiez l'intérieur de votre main droite
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Capture Buttons */}
            {!rightPalmImage && !showWebcam && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex gap-3 justify-center mt-6"
              >
                <Button
                  onClick={() => setShowWebcam(true)}
                  className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white px-6 py-5"
                  data-testid="open-camera-btn"
                >
                  <Camera className="mr-2 h-5 w-5" />
                  Prendre une photo
                </Button>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-6 py-5"
                  data-testid="upload-btn"
                >
                  <Upload className="mr-2 h-5 w-5" />
                  Importer
                </Button>
              </motion.div>
            )}
          </div>

          {/* Analyze Button */}
          {rightPalmImage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 pt-0"
            >
              <Button
                onClick={analyzeImage}
                disabled={isAnalyzing}
                className="w-full py-6 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-lg font-semibold shadow-lg"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <div className="w-5 h-5 mr-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Analyser ma paume
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </motion.div>
          )}

          {/* Info Footer */}
          <div className="px-6 pb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800">
                <strong>Essai gratuit :</strong> Cette analyse vous donne un aperçu de ce que LUMINA peut révéler. 
                Créez un compte pour débloquer l'analyse complète et sauvegarder vos lectures.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Features Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 grid grid-cols-3 gap-4"
        >
          {[
            { icon: '💕', label: 'Amour' },
            { icon: '💼', label: 'Carrière' },
            { icon: '⚕️', label: 'Santé' }
          ].map((theme) => (
            <div
              key={theme.label}
              className="bg-white rounded-xl border border-emerald-200 p-4 text-center"
            >
              <span className="text-2xl block mb-2">{theme.icon}</span>
              <span className="text-sm text-gray-600">{theme.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default GuestScanPage;
